import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import Header from './components/Header';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import NoticeDetail from './components/NoticeDetail';
import DepartmentDetail from './components/DepartmentDetail';
import { ProtectedRoute } from './components/ProtectedRoute';
import EmployeeDashboard from './components/dashboard/EmployeeDashboard';
import AdminDashboard from './components/dashboard/AdminDashboard';
import {
  EmployeeComplaintsPage,
  AdminComplaintsPage,
} from './components/dashboard/ComplaintsPage';
import AdminCategories from './components/dashboard/AdminCategories';
import AdminProductCategories from './components/dashboard/AdminProductCategories';
import AdminProductSubCategory from './components/dashboard/AdminProductSubCategory';
import AdminProductsList from './components/dashboard/AdminProductsList';
import AdminProductAdd from './components/dashboard/AdminProductAdd';
import AdminProductEdit from './components/dashboard/AdminProductEdit';
import AdminEmployeesList from './components/dashboard/AdminEmployeesList';
import AdminEmployeeAdd from './components/dashboard/AdminEmployeeAdd';
import AdminEmployeeEdit from './components/dashboard/AdminEmployeeEdit';
import AdminEmployeeDetailsPage from './components/dashboard/AdminEmployeeDetailsPage';
import AdminNatureOfComplaintList from './components/dashboard/AdminNatureOfComplaintList';
import AdminNatureOfComplaintAdd from './components/dashboard/AdminNatureOfComplaintAdd';
import AdminNatureOfComplaintEdit from './components/dashboard/AdminNatureOfComplaintEdit';
import AdminOutletsList from './components/dashboard/AdminOutletsList';
import AdminOutletAdd from './components/dashboard/AdminOutletAdd';
import AdminOutletEdit from './components/dashboard/AdminOutletEdit';
import AdminSubCategory from './components/dashboard/AdminSubCategory';
import AdminLicencesList from './components/dashboard/AdminLicencesList';
import AdminBehaviourTracking from './components/dashboard/AdminBehaviourTracking';
import AdminEmployeeBehaviourTracking from './components/dashboard/AdminEmployeeBehaviourTracking';
import BillToFlowWizard from './components/dashboard/BillToFlowWizard';
import AdminMyTools from './components/dashboard/AdminMyTools';
import AdminMyToolsView from './components/dashboard/AdminMyToolsView';
import AdminDailySales from './components/dashboard/AdminDailySales';
import AdminDailySalesAdd from './components/dashboard/AdminDailySalesAdd';
import AdminDailySalesView from './components/dashboard/AdminDailySalesView';
// import BackendDashboard from './components/dashboard/BackendDashboard';
// import BackendAssignedComplaintsPage from './components/dashboard/BackendAssignedComplaintsPage';
// import BackendComplaintUpdatesPage from './components/dashboard/BackendComplaintUpdatesPage';
import UserProfilePage from './components/dashboard/UserProfilePage';
import LeaveManagementPage from './components/dashboard/LeaveManagementPage';
import { useAuth } from './context/AuthContext';
import { employeeApi } from './redux/employeeApi';
import { bannerApi, useGetBannersQuery } from './redux/bannerApi';
import { getBannerSlides, preloadBannerSlides } from './utils/bannerSlides';

function DashboardIndexRedirect() {
  const { area, can, roleKey, canBehaviourTrack } = useAuth();
  const isSuperAdmin = area === 'admin';

  if (
    String(roleKey || '').toLowerCase() === 'backend' ||
    String(area || '').toLowerCase() === 'backend'
  ) {
    return <Navigate to="/dashboard/backend" replace />;
  }

  if (can('dashboard'))
    return (
      <Navigate
        to={isSuperAdmin ? '/dashboard/admin' : '/dashboard/employee'}
        replace
      />
    );
  if (can('complaints'))
    return (
      <Navigate
        to={isSuperAdmin ? '/dashboard/admin/complaints' : '/dashboard/employee/complaints'}
        replace
      />
    );
  if (can('leave_management')) {
    const leavePath =
      String(roleKey || '').toLowerCase() === 'backend'
        ? '/dashboard/backend/leave-management'
        : isSuperAdmin
          ? '/dashboard/admin/leave-management'
          : '/dashboard/employee/leave-management';
    return <Navigate to={leavePath} replace />;
  }
  // Admin modules should be driven by permissions (not by area).
  if (can('categories')) return <Navigate to="/dashboard/admin/categories" replace />;
  if (can('product_category'))
    return <Navigate to="/dashboard/admin/product-category" replace />;
  if (can('employees')) return <Navigate to="/dashboard/admin/employees" replace />;
  if (can('outsource_employee'))
    return <Navigate to="/dashboard/admin/outsource-employee" replace />;
  if (can('issue_types'))
    return <Navigate to="/dashboard/admin/issue-type" replace />;
  if (can('outlets')) return <Navigate to="/dashboard/admin/outlets" replace />;
  if (can('products')) return <Navigate to="/dashboard/admin/products" replace />;
  if (can('licences')) return <Navigate to="/dashboard/admin/licences" replace />;
  if (canBehaviourTrack) return <Navigate to="/dashboard/admin/employees" replace />;
  if (can('behaviour_tracking'))
    return <Navigate to="/dashboard/admin/behaviour-tracking" replace />;

  return <Navigate to="/landing" replace />;
}

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isAuthPage = location.pathname === '/login';

  const { data: bannersRes } = useGetBannersQuery();

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(employeeApi.util.prefetch('getActiveDepartments', undefined));
    dispatch(bannerApi.util.prefetch('getBanners', undefined));
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (!bannersRes) return;
    const landing = getBannerSlides(bannersRes, 'landing', { size: 'large' });
    const login = getBannerSlides(bannersRes, 'login', { size: 'large' });

    if (landing[0]) preloadBannerSlides([landing[0]]);
    if (login[0]) preloadBannerSlides([login[0]]);
  }, [bannersRes]);

  return (
    <div className="flex min-h-screen flex-col">
      {!isDashboard && !isAuthPage && <Header />}
      <main className={`flex-1 ${isDashboard ? 'min-h-screen' : ''}`}>
        <Routes>
          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? '/landing' : '/login'} replace />}
          />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/landing"
            element={
              <ProtectedRoute>
                <LandingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/notice/:id" element={<NoticeDetail />} />
          <Route path="/department/:id" element={<DepartmentDetail />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardIndexRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/employee"
            element={
              <ProtectedRoute requiredPermission="dashboard" requiredMenuAction="view">
                <EmployeeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/employee/complaints"
            element={
              <ProtectedRoute requiredPermission="complaints" requiredMenuAction="view">
                <EmployeeComplaintsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/employee/leave-management"
            element={
              <ProtectedRoute requiredPermission="leave_management" requiredMenuAction="view">
                <LeaveManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute requiredPermission="dashboard" requiredMenuAction="view">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/categories"
            element={
              <ProtectedRoute requiredPermission="categories" requiredMenuAction="view">
                <AdminCategories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/product-category"
            element={
              <ProtectedRoute requiredPermission="product_category" requiredMenuAction="view">
                <AdminProductCategories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/product-sub-category"
            element={
              <ProtectedRoute requiredPermission="product_category" requiredMenuAction="view">
                <AdminProductSubCategory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/sub-category"
            element={
              <ProtectedRoute requiredPermission="categories" requiredMenuAction="view">
                <AdminSubCategory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/employees"
            element={
              <ProtectedRoute requiredPermission="employees" requiredMenuAction="view">
                <AdminEmployeesList key="employees" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/employees/add"
            element={
              <ProtectedRoute requiredPermission="employees" requiredMenuAction="create">
                <AdminEmployeeAdd />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/employees/edit/:id"
            element={
              <ProtectedRoute requiredPermission="employees" requiredMenuAction="edit">
                <AdminEmployeeEdit />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/admin/employees/:id/behaviour-tracking"
            element={
              <ProtectedRoute requiredPermission="employees" requiredMenuAction="view">
                <AdminEmployeeBehaviourTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/employees/:id"
            element={
              <ProtectedRoute requiredPermission="employees" requiredMenuAction="view">
                <AdminEmployeeDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/outsource-employee"
            element={
              <ProtectedRoute requiredPermission="outsource_employee" requiredMenuAction="view">
                <AdminEmployeesList key="outsource" listType="outsource" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/outsource-employee/edit/:id"
            element={
              <ProtectedRoute requiredPermission="outsource_employee" requiredMenuAction="edit">
                <AdminEmployeeEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/outsource-employee/:id"
            element={
              <ProtectedRoute requiredPermission="outsource_employee" requiredMenuAction="view">
                <AdminEmployeeDetailsPage viewType="outsource" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/build-to-flow"
            element={
              <ProtectedRoute>
                <AdminMyTools />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/build-to-flow/create"
            element={
              <ProtectedRoute>
                <BillToFlowWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/build-to-flow/my-tools"
            element={<Navigate to="/dashboard/admin/build-to-flow" replace />}
          />
          <Route
            path="/dashboard/admin/build-to-flow/my-tools/:id"
            element={
              <ProtectedRoute>
                <AdminMyToolsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/build-to-flow/daily-sales"
            element={
              <ProtectedRoute>
                <AdminDailySales />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/build-to-flow/daily-sales/add"
            element={
              <ProtectedRoute>
                <AdminDailySalesAdd />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/build-to-flow/daily-sales/:id/edit"
            element={
              <ProtectedRoute>
                <AdminDailySalesAdd />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/build-to-flow/daily-sales/:id"
            element={
              <ProtectedRoute>
                <AdminDailySalesView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/complaints"
            element={
              <ProtectedRoute requiredPermission="complaints" requiredMenuAction="view">
                <AdminComplaintsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/leave-management"
            element={
              <ProtectedRoute requiredPermission="leave_management" requiredMenuAction="view">
                <LeaveManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/issue-type"
            element={
              <ProtectedRoute requiredPermission="issue_types" requiredMenuAction="view">
                <AdminNatureOfComplaintList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/issue-type/add"
            element={
              <ProtectedRoute requiredPermission="issue_types" requiredMenuAction="create">
                <AdminNatureOfComplaintAdd />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/issue-type/edit/:id"
            element={
              <ProtectedRoute requiredPermission="issue_types" requiredMenuAction="edit">
                <AdminNatureOfComplaintEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/nature-of-complaint/*"
            element={<Navigate to="/dashboard/admin/issue-type" replace />}
          />
          <Route
            path="/dashboard/admin/outlets"
            element={
              <ProtectedRoute requiredPermission="outlets" requiredMenuAction="view">
                <AdminOutletsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/outlets/add"
            element={
              <ProtectedRoute requiredPermission="outlets" requiredMenuAction="create">
                <AdminOutletAdd />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/outlets/edit/:id"
            element={
              <ProtectedRoute requiredPermission="outlets" requiredMenuAction="edit">
                <AdminOutletEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/licences"
            element={
              <ProtectedRoute requiredPermission="licences" requiredMenuAction="view">
                <AdminLicencesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/behaviour-tracking/:trackingId/edit"
            element={
              <ProtectedRoute requiredPermission="behaviour_tracking" requiredMenuAction="edit">
                <AdminEmployeeBehaviourTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/behaviour-tracking"
            element={
              <ProtectedRoute requiredPermission="behaviour_tracking" requiredMenuAction="view">
                <AdminBehaviourTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/products"
            element={
              <ProtectedRoute requiredPermission="products" requiredMenuAction="view">
                <AdminProductsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/products/add"
            element={
              <ProtectedRoute requiredPermission="products" requiredMenuAction="create">
                <AdminProductAdd />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/products/edit/:id"
            element={
              <ProtectedRoute requiredPermission="products" requiredMenuAction="edit">
                <AdminProductEdit />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/dashboard/backend"
            element={
              <ProtectedRoute
                requiredRole="backend"
                requiredPermission="dashboard"
                requiredMenuAction="view"
              >
                <BackendDashboard />
              </ProtectedRoute>
            }
          /> */}
          {/* <Route
            path="/dashboard/backend/complaints"
            element={
              <ProtectedRoute
                requiredRole="backend"
                requiredPermission="complaints"
                requiredMenuAction="view"
              >
                <BackendAssignedComplaintsPage />
              </ProtectedRoute>
            }
          /> */}
          {/* <Route
            path="/dashboard/backend/leave-management"
            element={
              <ProtectedRoute
                requiredRole="backend"
                requiredPermission="leave_management"
                requiredMenuAction="view"
              >
                <LeaveManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/backend/complaints/:id/edit"
            element={
              <ProtectedRoute
                requiredRole="backend"
                requiredPermission="complaints"
                requiredMenuAction="view"
              >
                <BackendComplaintUpdatesPage />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/dashboard/profile"
            element={
              <ProtectedRoute>
                <UserProfilePage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </main>
      {!isDashboard && !isAuthPage && <Footer />}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default App;
